import { BaseApiResponse, ChatChannelWithParticipant, OptionsApi } from '@core/swagger-client';
import { ChatChannelIds, ChatMessage, Participant } from '@core/types';
import {
    Button,
    CompositePageHeader,
    ContainerNoTopSpace,
    ErrorMessage,
    ErrorMessagePage,
    Heading,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Select,
    Table,
    useCreateColumnHelper
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildChatExportRows, buildChatWorkbook, getChannelName, getParticipantName } from './chatExport';

export const ChatMessages = () => {
    const { t } = useTranslation();
    const { webinarId } = useParams({ strict: false });
    const queryClient = useQueryClient();

    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: channels, isLoading: isChannelsLoading, error: channelsError } = useQuery(OptionsApi.insights.getChatMessages(webinarId!));
    const [channelId, setChannelId] = useState<string>(ChatChannelIds.allChannel);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [participant, setParticipant] = useState<Participant>();

    const columnHelper = useCreateColumnHelper<ChatMessage>();
    const columns = [
        columnHelper.accessor((row) => row.timestamp, {
            id: 'timestamp',
            header: () => <>Time</>,
            cell: (info) => new Date(info.getValue()).toLocaleTimeString()
        }),
        columnHelper.accessor((row) => row.senderNickname, {
            id: 'senderNickname',
            header: () => <>Sender</>
        }),
        columnHelper.accessor((row) => row.message, {
            id: 'message',
            header: () => <>Message</>
        })
    ];

    useEffect(() => {
        if (!channels) {
            setChatMessages([]);
            setParticipant(undefined);
            return;
        }

        if (channelId === ChatChannelIds.allChannel) {
            const messages = channels.reduce((acc, channel) => acc.concat(channel.messages), [] as ChatMessage[]);
            setChatMessages(messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()));
            setParticipant(undefined);
        } else {
            const channel = channels.find((channel) => channel.channelId === channelId);
            if (channel) {
                setChatMessages(channel.messages);
                setParticipant(channel.participant);
            } else {
                setChatMessages([]);
                setParticipant(undefined);
            }
        }
    }, [channelId, channels]);

    // Participants (with their e-mail addresses) are only needed for the export, so they are fetched on click
    const exportMutation = useMutation<void, BaseApiResponse, { channels: ChatChannelWithParticipant[]; webinarTitle: string }>({
        mutationFn: async ({ channels, webinarTitle }) => {
            const participants = await queryClient.fetchQuery(OptionsApi.insights.getParticipants(webinarId!));
            const xlsx = await import('xlsx');
            const channel = channels.find((c) => c.channelId === channelId);
            const rows = buildChatExportRows(channels, channelId, participants);
            const workbook = buildChatWorkbook(xlsx, rows, webinarTitle, channel ? getChannelName(channel) : 'All chat');
            xlsx.writeFileXLSX(workbook, 'ChatMessages.xlsx');
        }
    });

    const error = webinarError ?? channelsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || !webinar || isChannelsLoading || !channels) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>

            <div className="px:4 md:px-6 pt-4 md:pt-10 max-w-4xl">
                <BackToOriginLink fallback="insights" />
                <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                    {t('Insights.Chat.PageHeading')}
                </Heading>
                <Select label="Select a channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                    <option value={ChatChannelIds.allChannel}>All chat</option>
                    {channels
                        .sort((a, b) => a.channelId.localeCompare(b.channelId))
                        .map((channel) => (
                            <option key={channel.channelId} value={channel.channelId}>
                                {getChannelName(channel)}
                            </option>
                        ))}
                </Select>
                <div className="flex justify-between flex-wrap gap-4 my-8">
                    <p className="bg-white rounded-lg flex items-end py-2 px-4">
                        {t('Insights.Chat.AmountText')}
                        <span className="text-3xl font-medium pl-2">{chatMessages.length}</span>
                    </p>
                    <Button onClick={() => exportMutation.mutate({ channels, webinarTitle: webinar.title })} disabled={exportMutation.isPending} variant="primary">
                        {t('Insights.Chat.Button.Export')}
                    </Button>
                </div>
                {exportMutation.error && <ErrorMessage apiError={exportMutation.error} classNames="mb-8" />}
                {participant && (
                    <>
                        <div>
                            ParticipantId: {participant.participantId}
                            <br />
                            Participant: {getParticipantName(participant)}
                            <br />
                            Firstname: {participant.firstName}
                            <br />
                            Lastname: {participant.lastName}
                            <br />
                            Email: {participant.emailAddress}
                            <br />
                            Company: {participant.companyName}
                            <br />
                            Country: {participant.countryCode}
                            <br />
                            Time online: {participant.timeOnlineLive}
                            <br />
                        </div>
                    </>
                )}
                <Table columns={columns} data={chatMessages} />
            </div>
        </ContainerNoTopSpace>
    );
};
